import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon,
  DocumentTextIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../../components/LoadingSpinner'

export default function ProviderSettingsPage() {
  const [activeTab, setActiveTab] = useState('general')
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Settings state
  const [generalSettings, setGeneralSettings] = useState({
    autoApprovalEnabled: false,
    maxProvidersPerCity: 50,
    defaultProviderRating: 0.0,
    requireVerification: true,
    allowSelfRegistration: true
  })

  const [approvalSettings, setApprovalSettings] = useState({
    requireDocuments: true,
    requiredDocuments: ['Business License', 'Insurance Certificate', 'Service Certification'],
    approvalTimeLimit: 7, // days
    autoRejectAfterLimit: true,
    notificationEmails: ['admin@akhirat.co', 'providers@akhirat.co']
  })

  const [notificationSettings, setNotificationSettings] = useState({
    newProviderNotification: true,
    approvalStatusChange: true,
    providerRatingChange: true,
    documentExpiration: true,
    systemAlerts: true
  })

  const [serviceSettings, setServiceSettings] = useState({
    ambulanceRequirements: {
      minVehicles: 1,
      insuranceRequired: true,
      driverCertification: true,
      vehicleInspection: true
    },
    ustadzRequirements: {
      certificationRequired: true,
      minimumExperience: 2, // years
      backgroundCheck: true
    },
    cemeteryRequirements: {
      ownershipProof: true,
      operatingLicense: true,
      sizeMinimum: 1000 // square meters
    },
    vendorRequirements: {
      businessLicense: true,
      taxRegistration: true,
      qualityStandards: true
    }
  })

  const tabs = [
    { id: 'general', label: 'General Settings', icon: Cog6ToothIcon },
    { id: 'approval', label: 'Approval Workflow', icon: ShieldCheckIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'service', label: 'Service Requirements', icon: DocumentTextIcon },
  ]

  const handleSave = async () => {
    setSaving(true)
    try {
      // TODO: Implement API call to save settings
      await new Promise(resolve => setTimeout(resolve, 1500))
      setHasChanges(false)
    } catch (error) {
      console.error('Failed to save settings:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    // TODO: Reset to default values
    setHasChanges(false)
  }

  const markAsChanged = () => {
    setHasChanges(true)
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Provider Settings</h1>
            <p className="text-gray-600 mt-1">Configure provider management policies and workflows</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Reset to Default
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
            <Link
              to="/admin/providers"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Back to Providers
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon
                  className={`mr-2 h-5 w-5 ${
                    activeTab === tab.id
                      ? 'text-primary-500'
                      : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">General Provider Settings</h3>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Auto-Approval</h4>
                    <p className="text-sm text-gray-500">Automatically approve new provider applications</p>
                  </div>
                  <button
                    onClick={() => {
                      setGeneralSettings(prev => ({ ...prev, autoApprovalEnabled: !prev.autoApprovalEnabled }))
                      markAsChanged()
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      generalSettings.autoApprovalEnabled ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        generalSettings.autoApprovalEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Providers Per City
                  </label>
                  <input
                    type="number"
                    value={generalSettings.maxProvidersPerCity}
                    onChange={(e) => {
                      setGeneralSettings(prev => ({ ...prev, maxProvidersPerCity: parseInt(e.target.value) || 0 }))
                      markAsChanged()
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Limit the number of providers in each city</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Require Document Verification</h4>
                    <p className="text-sm text-gray-500">Mandatory document verification for all providers</p>
                  </div>
                  <button
                    onClick={() => {
                      setGeneralSettings(prev => ({ ...prev, requireVerification: !prev.requireVerification }))
                      markAsChanged()
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      generalSettings.requireVerification ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        generalSettings.requireVerification ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Allow Self-Registration</h4>
                    <p className="text-sm text-gray-500">Enable providers to register themselves</p>
                  </div>
                  <button
                    onClick={() => {
                      setGeneralSettings(prev => ({ ...prev, allowSelfRegistration: !prev.allowSelfRegistration }))
                      markAsChanged()
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      generalSettings.allowSelfRegistration ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        generalSettings.allowSelfRegistration ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'approval' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Approval Workflow Settings</h3>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Require Documents for Approval</h4>
                    <p className="text-sm text-gray-500">Mandatory document submission during approval</p>
                  </div>
                  <button
                    onClick={() => {
                      setApprovalSettings(prev => ({ ...prev, requireDocuments: !prev.requireDocuments }))
                      markAsChanged()
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      approvalSettings.requireDocuments ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        approvalSettings.requireDocuments ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Time Limit (Days)
                  </label>
                  <input
                    type="number"
                    value={approvalSettings.approvalTimeLimit}
                    onChange={(e) => {
                      setApprovalSettings(prev => ({ ...prev, approvalTimeLimit: parseInt(e.target.value) || 0 }))
                      markAsChanged()
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Days before auto-rejection of pending applications</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Auto-Reject After Time Limit</h4>
                    <p className="text-sm text-gray-500">Automatically reject applications after time limit</p>
                  </div>
                  <button
                    onClick={() => {
                      setApprovalSettings(prev => ({ ...prev, autoRejectAfterLimit: !prev.autoRejectAfterLimit }))
                      markAsChanged()
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      approvalSettings.autoRejectAfterLimit ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        approvalSettings.autoRejectAfterLimit ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notification Emails
                  </label>
                  <div className="space-y-2">
                    {approvalSettings.notificationEmails.map((email, index) => (
                      <div key={index} className="flex space-x-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const newEmails = [...approvalSettings.notificationEmails]
                            newEmails[index] = e.target.value
                            setApprovalSettings(prev => ({ ...prev, notificationEmails: newEmails }))
                            markAsChanged()
                          }}
                          className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="admin@akhirat.co"
                        />
                        {approvalSettings.notificationEmails.length > 1 && (
                          <button
                            onClick={() => {
                              const newEmails = approvalSettings.notificationEmails.filter((_, i) => i !== index)
                              setApprovalSettings(prev => ({ ...prev, notificationEmails: newEmails }))
                              markAsChanged()
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setApprovalSettings(prev => ({ ...prev, notificationEmails: [...prev.notificationEmails, ''] }))
                        markAsChanged()
                      }}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      + Add Email
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>

              <div className="space-y-4">
                {Object.entries(notificationSettings).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {key === 'newProviderNotification' && 'Notify when new providers register'}
                        {key === 'approvalStatusChange' && 'Notify when provider approval status changes'}
                        {key === 'providerRatingChange' && 'Notify when provider ratings are updated'}
                        {key === 'documentExpiration' && 'Notify when provider documents are expiring'}
                        {key === 'systemAlerts' && 'Send critical system alerts and notifications'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setNotificationSettings(prev => ({ ...prev, [key]: !value }))
                        markAsChanged()
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        value ? 'bg-primary-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          value ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'service' && (
          <div className="space-y-6">
            {/* Ambulance Requirements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Ambulance Service Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Vehicles</label>
                  <input
                    type="number"
                    value={serviceSettings.ambulanceRequirements.minVehicles}
                    onChange={(e) => {
                      setServiceSettings(prev => ({
                        ...prev,
                        ambulanceRequirements: {
                          ...prev.ambulanceRequirements,
                          minVehicles: parseInt(e.target.value) || 0
                        }
                      }))
                      markAsChanged()
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.ambulanceRequirements.insuranceRequired}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          ambulanceRequirements: {
                            ...prev.ambulanceRequirements,
                            insuranceRequired: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Insurance Required</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.ambulanceRequirements.driverCertification}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          ambulanceRequirements: {
                            ...prev.ambulanceRequirements,
                            driverCertification: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Driver Certification Required</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.ambulanceRequirements.vehicleInspection}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          ambulanceRequirements: {
                            ...prev.ambulanceRequirements,
                            vehicleInspection: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Vehicle Inspection Required</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Ustadz Requirements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Ustadz Service Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Experience (Years)</label>
                  <input
                    type="number"
                    value={serviceSettings.ustadzRequirements.minimumExperience}
                    onChange={(e) => {
                      setServiceSettings(prev => ({
                        ...prev,
                        ustadzRequirements: {
                          ...prev.ustadzRequirements,
                          minimumExperience: parseInt(e.target.value) || 0
                        }
                      }))
                      markAsChanged()
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.ustadzRequirements.certificationRequired}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          ustadzRequirements: {
                            ...prev.ustadzRequirements,
                            certificationRequired: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Certification Required</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.ustadzRequirements.backgroundCheck}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          ustadzRequirements: {
                            ...prev.ustadzRequirements,
                            backgroundCheck: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Background Check Required</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Cemetery Requirements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Cemetery Service Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Size (sqm)</label>
                  <input
                    type="number"
                    value={serviceSettings.cemeteryRequirements.sizeMinimum}
                    onChange={(e) => {
                      setServiceSettings(prev => ({
                        ...prev,
                        cemeteryRequirements: {
                          ...prev.cemeteryRequirements,
                          sizeMinimum: parseInt(e.target.value) || 0
                        }
                      }))
                      markAsChanged()
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.cemeteryRequirements.ownershipProof}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          cemeteryRequirements: {
                            ...prev.cemeteryRequirements,
                            ownershipProof: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Ownership Proof Required</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={serviceSettings.cemeteryRequirements.operatingLicense}
                      onChange={(e) => {
                        setServiceSettings(prev => ({
                          ...prev,
                          cemeteryRequirements: {
                            ...prev.cemeteryRequirements,
                            operatingLicense: e.target.checked
                          }
                        }))
                        markAsChanged()
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Operating License Required</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Requirements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Vendor Service Requirements</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={serviceSettings.vendorRequirements.businessLicense}
                    onChange={(e) => {
                      setServiceSettings(prev => ({
                        ...prev,
                        vendorRequirements: {
                          ...prev.vendorRequirements,
                          businessLicense: e.target.checked
                        }
                      }))
                      markAsChanged()
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Business License Required</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={serviceSettings.vendorRequirements.taxRegistration}
                    onChange={(e) => {
                      setServiceSettings(prev => ({
                        ...prev,
                        vendorRequirements: {
                          ...prev.vendorRequirements,
                          taxRegistration: e.target.checked
                        }
                      }))
                      markAsChanged()
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Tax Registration Required</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={serviceSettings.vendorRequirements.qualityStandards}
                    onChange={(e) => {
                      setServiceSettings(prev => ({
                        ...prev,
                        vendorRequirements: {
                          ...prev.vendorRequirements,
                          qualityStandards: e.target.checked
                        }
                      }))
                      markAsChanged()
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Quality Standards Compliance Required</label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Settings Information</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Changes to provider settings will affect all new provider registrations and approval processes.
              Existing providers will be notified of any requirement changes.</p>
              <p className="mt-1">Some settings may require application restart to take effect.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}